import { useEffect } from 'preact/hooks';
import register from '@wcd/preact-custom-element';
import { isLoggedIn } from '../shared/cookies';

import { DownloadedResponse, ResourceResponse, UserResponse } from '../../../types/api';
import { ResourceDetailContext as ComponentDefinition } from '../../../types/web-components';
import { CurrentLicencesResponse } from '../../../types/api/current-licences-response.type';

const siteCountry = (window.location.pathname.match(/^\/en-(.*?)\/.*/) || ['', 'gb'])[1].toLowerCase();

const fetchData = async <T extends unknown>(url): Promise<T> => await (await fetch(url, {})).json();

const userDefault: UserResponse = { owns: false, saved: false, subscriptions: [] };
const currentLicencesDefault: CurrentLicencesResponse = { isSchoolUpgradeAvailable: false, hasSchoolLicence: false, licences: [] };

type ResourceDetailContext = {
  isLoading: boolean;
  resource: ResourceResponse;
  user: UserResponse;
  downloaded: DownloadedResponse,
  currentLicences: CurrentLicencesResponse
};

let context: ResourceDetailContext;
let setContext;
const loading = new Promise<ResourceDetailContext>((resolve) => {
  setContext = (value) => {
    if (!context) context = value;
    resolve(context);
  };
});

export const getContext = (): Promise<ResourceDetailContext> => (context ? Promise.resolve(context) : loading);

type useContextCallbackFunction = (c: ResourceDetailContext) => void;

export const useContext = (resolve: useContextCallbackFunction) => {
  const effect = async () => resolve(await getContext());
  useEffect(() => {
    effect();
  }, []);
};

const load = async (resourceid) => {
  const [resource, user, downloaded, currentLicences] = await Promise.all([
    fetchData<ResourceResponse>(`api/resource/${resourceid}?siteCountry=${siteCountry}`),
    isLoggedIn ? fetchData<UserResponse>(`api/resource/${resourceid}/user`) : Promise.resolve(userDefault),
    isLoggedIn ? fetchData<DownloadedResponse>(`api/downloaded/${resourceid}`) : Promise.resolve(null),
    isLoggedIn ? fetchData<CurrentLicencesResponse>(`api/current-licences/${resourceid}`) : Promise.resolve(currentLicencesDefault),
  ]);

  setContext({ isLoggedIn, resource, user, downloaded, currentLicences });
};

export const ResourceDetailContext: ComponentDefinition = ({ resourceid, children }) => {
  useEffect(() => {
    load(resourceid);
  }, []);

  return children;
};

register(ResourceDetailContext, 'tes-resource-detail-context', ['resourceid'], { shadow: false });
